/* eslint-disable @typescript-eslint/no-explicit-any */
import { useField } from "formik";
import React, { useEffect } from "react";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";

import { DropdownOption } from "../../helpers/optionSets";
import * as commonStyles from "../commonStyles/commonStyles.module.css";
import { FieldErrorMessage } from "../FieldErrorMessage";
import { FieldHeader, FieldHeaderProps, FieldStateProps } from "../FieldHeader";
import { SelectDropdown } from "../SelectDropdown/SelectDropdown";
import { useSetFieldTouched } from "../useSetFieldTouched";

export type DropdownFieldProps = Readonly<{
  values: Array<DropdownOption>;
  name: string;
  type?: "string" | "number";
  onSelectedCountryOrRegion?: (vale: string) => void;
  renderDisabledAsDropdown?: boolean;
}> &
  FieldHeaderProps &
  FieldStateProps;

export const DropdownField = (props: DropdownFieldProps) => {
  const { values, name, type, disabled, onSelectedCountryOrRegion } = props;
  const [field, _meta, helpers] = useField(props);
  useSetFieldTouched(field.name);

  const handleChange = (id: string) => {
    // SubForm sets validateOnChange={false}, so selecting a value won't re-run
    // validation on its own — pass shouldValidate=true to clear a stale
    // "Required" error the moment a valid option is chosen.
    if (type === "number") {
      helpers.setValue(parseInt(id, 10), true);
    } else {
      helpers.setValue(id, true);
    }
  };

  useEffect(() => {
    if (field.value && onSelectedCountryOrRegion) {
      onSelectedCountryOrRegion(field.value);
    }
  }, [field.value, name, onSelectedCountryOrRegion]);

  const getCurrentLabel = () => {
    const selectedOption = values.find(option => option.value === field.value);
    return selectedOption?.label ?? "";
  };

  const options = values.map(v => ({ id: String(v.value), label: v.label }));

  return (
    <Row className={commonStyles.container}>
      <Column>
        <FieldHeader {...props} />
        {disabled && !props.renderDisabledAsDropdown && <span>{getCurrentLabel()}</span>}
        {(!disabled || props.renderDisabledAsDropdown) && (
          <SelectDropdown
            options={options}
            value={String(field.value ?? "")}
            onChange={handleChange}
            placeholder="Select"
            disabled={disabled || props.isSaving}
            aria-label={name}
          />
        )}
        <FieldErrorMessage fieldName={field.name} />
      </Column>
    </Row>
  );
};
